import {
  useCreateSelectionDateMutation,
  useGetRulesMutation,
  useGetRulesVerificationMutation,
  useGetSelectionDatesMutation,
  useUpdateSelectionDateMutation,
} from "src/services/selectionDatesServices";
import {
  MediumLargeHeadingStyled,
  SectionHeaderStyled,
  SmallHeadingStyled,
} from "../../../components/custom/texts/texts.styled";
import {
  Button,
  Card,
  Divider,
  Dropdown,
  Flex,
  Form,
  Icon,
  InputNumber,
  Text,
} from "@nubeteck/components";
import { Collapse, Input, Layout, List, Table, Select, Drawer, Skeleton } from "antd";
import { ColumnsType } from "antd/es/table";
import React, { useEffect } from "react";
import toast from "react-hot-toast";
import { FormInstance } from "antd/lib/form";
import { useErrorSavingDataToast, useSuccessRegisterToast, toastErrorStyle } from "src/constants";
import ConfigurationInput from "../generalSettings/settingsInput";
import { IPeriodosFechas } from "@/Interfaces/datesSettings";
import { PeriodEstimationVariants } from "../period-estimation-variants";
import { ArrayUtils } from "@nubeteck/utils";
import { useGetAdminPeriodsQuery } from "src/services";
import { useGetCareersByStudentIdMutation } from "src/services/importsService";
import { getImportDataPage } from "../../../Api/content";
import { useLocale } from "../../../i18n/localeContext";
import { StrapiError } from "../../StrapiError";
import { _translateValue } from "../translation-gestor/translateValue";
import { formatDate } from "src/utils/date-util";

const { Content } = Layout;
const SCREEN_NAME = "PantallaDateSettings";

const DateSettings: React.FC = () => {
  const [form] = Form.useForm<IPeriodosFechas>();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [procesoId, setProcesoId] = React.useState(0);
  const [periodoId, setPeriodoId] = React.useState(0);
  const [matriculaVerificacion, setMatriculaVerificacion] = React.useState<string>();
  const [curriculumVerificacion, setCurriculumVerificacion] = React.useState<string>();
  const [periodoVerificacion, setPeriodoVerificacion] = React.useState<string>();
  const [anioVerificacion, setAnioVerificacion] = React.useState<number>();
  const [periodoVariantes, setPeriodoVariantes] = React.useState<IPeriodosFechas>();
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [drawerVerificacionOpen, setDrawerVerificacionOpen] = React.useState(false);
  const { locale } = useLocale();
  const [importDataPage, setImportDataPage] = React.useState<any>(null);
  const [isLoadingContent, setIsLoadingContent] = React.useState<boolean>(true);
  const [hasError, setHasError] = React.useState(false);
  const showSuccessToast = useSuccessRegisterToast();
  const showErrorSavingToast = useErrorSavingDataToast();
  const [translatedData, setTranslatedData] = React.useState<{
    estados: { [key: string]: string };
    limitados: { [key: string]: string };
    reglas: { [key: string]: string };
  }>({
    estados: {},
    limitados: {},
    reglas: {},
  });

  const [anioFiltro, setAnioFiltro] = React.useState<number>(new Date().getFullYear());

  const formRef = React.useRef<FormInstance>(null);
  const [getDates, { data, isLoading }] = useGetSelectionDatesMutation();
  const [getRules, { data: rulesData }] = useGetRulesMutation();
  const [getCareersByStudentId, { isLoading: isLoadingCareers, data: dataCareers }] =
    useGetCareersByStudentIdMutation();

  const { data: periodos } = useGetAdminPeriodsQuery(anioVerificacion, {
    refetchOnMountOrArgChange: true,
  });
  const [
    createDate,
    { isLoading: isLoadingCreate, isSuccess: isSuccessCreate, isError: isErrorCreate },
  ] = useCreateSelectionDateMutation();
  const [
    updateDate,
    { isLoading: isLoadingUpdate, isSuccess: isSuccessUpdate, isError: isErrorUpdate },
  ] = useUpdateSelectionDateMutation();
  const [
    getRulesVerification,
    { data: rulesVerification, isLoading: isLoadingVerification, error: errorVerification },
  ] = useGetRulesVerificationMutation();

  const carrerasOptions = ArrayUtils.selectLabelValue(
    dataCareers?.data ?? [],
    "carreraNombre",
    "carreraId",
  );

  useEffect(() => {
    let cancel = false;
    setHasError(false);
    setIsLoadingContent(true);
    
    const timeoutId = setTimeout(() => {
      if (!cancel) {
        setImportDataPage(null);
        setIsLoadingContent(false);
        setHasError(locale === 'en');
      }
    }, 3000);
    
    getImportDataPage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          clearTimeout(timeoutId);
          setImportDataPage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => {
        if (!cancel) {
          clearTimeout(timeoutId);
          setImportDataPage(null);
          setIsLoadingContent(false);
          setHasError(locale === 'en');
        }
      });
    return () => {
      cancel = true;
      clearTimeout(timeoutId);
    };
  }, [locale]);

  React.useEffect(() => {
    if (anioFiltro) {
      getDates(anioFiltro);
    }
  }, [getDates, isSuccessCreate, isSuccessUpdate, isErrorCreate, isErrorUpdate, anioFiltro]);

  // Efecto para traducir los campos traducibles cuando lleguen los datos o cambie el locale
  useEffect(() => {
    const translateData = async () => {
      // Verificar si hay datos para traducir
      const hasData = data?.data && Array.isArray(data.data) && data.data.length > 0;

      // Si no hay datos, no hacer nada
      if (!hasData) {
        setTranslatedData((prev) => ({
          ...prev,
          estados: {},
          limitados: {},
        }));
        return;
      }

      const estadosTranslations: { [key: string]: string | null } = {};
      const limitadosTranslations: { [key: string]: string | null } = {};

      const translationPromises: Promise<void>[] = [];

      // Recopilar todos los valores únicos que necesitan traducción
      data.data.forEach((record: IPeriodosFechas) => {
        // Traducir estado (Abierto/Cerrado)
        const estadoText = record.estadoAbierta ? "Abierto" : "Cerrado";
        if (!estadosTranslations.hasOwnProperty(estadoText)) {
          estadosTranslations[estadoText] = null; // Marcador para evitar duplicados
          translationPromises.push(
            _translateValue(SCREEN_NAME, estadoText, locale).then((translated) => {
              estadosTranslations[estadoText] = translated;
            })
          );
        }

        // Traducir limitado a ciertas carreras (Si/No)
        const limitadoText = record.limitadoACarreras ? "Si" : "No";
        if (!limitadosTranslations.hasOwnProperty(limitadoText)) {
          limitadosTranslations[limitadoText] = null; // Marcador para evitar duplicados
          translationPromises.push(
            _translateValue(SCREEN_NAME, limitadoText, locale).then((translated) => {
              limitadosTranslations[limitadoText] = translated;
            })
          );
        }
      });

      await Promise.all(translationPromises);

      // Actualizar estados con las traducciones
      setTranslatedData((prev) => ({
        ...prev,
        estados: estadosTranslations as { [key: string]: string },
        limitados: limitadosTranslations as { [key: string]: string },
      }));
    };

    translateData();
  }, [data?.data, locale]);

  useEffect(() => {
    if (periodoId !== 0) {
      getRules(periodoId);
    }
  }, [getRules, periodoId]);

  // Efecto para traducir las descripciones de las reglas cuando lleguen los datos o cambie el locale
  useEffect(() => {
    const translateRules = async () => {
      // Verificar si hay datos para traducir
      const hasColones = rulesData?.data?.colones && Array.isArray(rulesData.data.colones) && rulesData.data.colones.length > 0;
      const hasDolares = rulesData?.data?.dolares && Array.isArray(rulesData.data.dolares) && rulesData.data.dolares.length > 0;

      // Si no hay datos, no hacer nada
      if (!hasColones && !hasDolares) {
        setTranslatedData((prev) => ({
          ...prev,
          reglas: {},
        }));
        return;
      }

      const reglasTranslations: { [key: string]: string | null } = {};
      const translationPromises: Promise<void>[] = [];

      // Recopilar todos los valores únicos que necesitan traducción
      if (hasColones) {
        rulesData.data.colones.forEach((ru: any) => {
          if (ru.descripcion && !reglasTranslations.hasOwnProperty(ru.descripcion)) {
            reglasTranslations[ru.descripcion] = null; // Marcador para evitar duplicados
            translationPromises.push(
              _translateValue(SCREEN_NAME, ru.descripcion, locale).then((translated) => {
                reglasTranslations[ru.descripcion] = translated;
              })
            );
          }
        });
      }

      if (hasDolares) {
        rulesData.data.dolares.forEach((ru: any) => {
          if (ru.descripcion && !reglasTranslations.hasOwnProperty(ru.descripcion)) {
            reglasTranslations[ru.descripcion] = null; // Marcador para evitar duplicados
            translationPromises.push(
              _translateValue(SCREEN_NAME, ru.descripcion, locale).then((translated) => {
                reglasTranslations[ru.descripcion] = translated;
              })
            );
          }
        });
      }

      await Promise.all(translationPromises);

      // Actualizar estados con las traducciones
      setTranslatedData((prev) => ({
        ...prev,
        reglas: reglasTranslations as { [key: string]: string },
      }));
    };

    translateRules();
  }, [rulesData?.data?.colones, rulesData?.data?.dolares, locale]);

  React.useEffect(() => {
    if (isErrorUpdate) showErrorSavingToast();

    if (isSuccessUpdate) {
      showSuccessToast();
      form.resetFields();
      setProcesoId(0);
    }
    setDrawerOpen(false);
  }, [isSuccessUpdate, form, isErrorUpdate, showSuccessToast, showErrorSavingToast]);
  React.useEffect(() => {
    if (errorVerification) toast.error(errorVerification?.data?.message, toastErrorStyle);
  }, [errorVerification]);

  const onClickEditarPeriodo = React.useCallback(
    (record: IPeriodosFechas) => {
      setProcesoId(record.procesoId);
      setPeriodoId(record.periodoId);
      form.setFieldsValue({
        terminosYCondiciones: record.terminosYCondiciones,
        reglaContado: record.reglaContado,
        reglaContadoDolares: record.reglaContadoDolares,
        reglaCuotas: record.reglaCuotas,
        reglaCuotasDolares: record.reglaCuotasDolares,
        reglaContadoAmazon: record.reglaContadoAmazon,
        reglaContadoAmazonDolares: record.reglaContadoAmazonDolares,
        reglaCuotasAmazon: record.reglaCuotasAmazon,
        reglaCuotasAmazonDolares: record.reglaCuotasAmazonDolares,
      });
      setDrawerOpen(true);
    },
    [form],
  );

  const submit = (values: any) => {
    if (procesoId !== 0) {
      updateDate({
        ...values,
        id: procesoId,
      });
    } else {
      createDate(values);
    }
  };

  const filterOption = (input: string, option?: { label: string; value: string }) =>
    (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  const periodosOptions = ArrayUtils.selectLabelValue(periodos?.data ?? [], "nombre", "idPeriodo");

  const columns = React.useMemo<ColumnsType<IPeriodosFechas>>(
    () => [
      {
        title: importDataPage?.select_period_table?.[0]?.label || "Año",
        dataIndex: "anio",
      },
      {
        title: importDataPage?.select_period_table?.[1]?.label || "Periodo",
        dataIndex: "periodoNombre",
      },
      {
        title: importDataPage?.select_period_table?.[2]?.label || "Fecha de inicio de regulares",
        dataIndex: "fechaInicio",
        render: (value) => formatDate(value),
      },
      {
        title: importDataPage?.select_period_table?.[3]?.label || "Fecha de fin de regulares",
        dataIndex: "fechaFin",
        render: (value) => formatDate(value),
      },
      {
        title: importDataPage?.select_period_table?.[4]?.label || "Fecha de inicio primer ingreso",
        dataIndex: "fechaInicioPrimerIngreso",
        render: (value) => formatDate(value),
      },
      {
        title: importDataPage?.select_period_table?.[5]?.label || "Fecha de fin primer ingreso",
        dataIndex: "fechaFinPrimerIngreso",
        render: (value) => formatDate(value),
      },
      {
        title: importDataPage?.select_period_table?.[6]?.label || "Estado",
        dataIndex: "estadoAbierta",
        render: (_, record) => {
          const estadoText = record.estadoAbierta ? "Abierto" : "Cerrado";
          return translatedData.estados[estadoText] || estadoText;
        },
      },
      {
        title: importDataPage?.select_period_table?.[7]?.label || "Limitado a ciertas carreras",
        dataIndex: "limitadoACarreras",
        render: (_, record) => {
          const limitadoText = record.limitadoACarreras ? "Si" : "No";
          return translatedData.limitados[limitadoText] || limitadoText;
        },
      },
      {
        title: "",
        dataIndex: "actions",
        fixed: "right",
        width: 100,
        render: (_, record) => (
          <Dropdown
            overlayClassName="table-dropdown"
            className="cursor-pointer"
            key={record.procesoId}
            trigger={["click"]}
            align={{ offset: [-40, 4] }}
            menu={{
              items: [
                {
                  key: "1",
                  label: importDataPage?.select_Periods?.edit_button || "Editar",
                  icon: <Icon name="" color="A7A7A7" />,
                  onClick: () => onClickEditarPeriodo(record),
                },
                {
                  key: "2",
                  label: importDataPage?.select_Periods?.variant_button || "Variantes",
                  icon: <Icon name="" color="A7A7A7" />,
                  // onClick: () => navigate(`period-estimation-variants/${record.periodoId}`),
                  onClick: () => {
                    setPeriodoVariantes(record);
                  },
                },
              ],
            }}
          >
            <Icon name="" isTouchable />
          </Dropdown>
        ),
      },
    ],
    [onClickEditarPeriodo, importDataPage?.select_period_table, importDataPage?.select_Periods, translatedData],
  );

  if (hasError && locale === 'en') {
    return <StrapiError 
      onRetry={() => {
        setHasError(false);
        setIsLoadingContent(true);
      }}
      onUseDefault={() => {
        setHasError(false);
        setIsLoadingContent(false);
        setImportDataPage(null);
      }}
    />;
  }

  return !periodoVariantes ? (
    <Flex style={{ flexDirection: "column", width: "100%", alignSelf: "flex-start" }}>
      <Content style={{ marginTop: "16px" }}>
        {isLoadingContent ? (
          <>
            <SectionHeaderStyled>
              <Skeleton.Input active size="large" style={{ width: 300, marginBottom: 16 }} />
              <Flex style={{ gap: "15px" }}>
                <Skeleton.Input active size="default" style={{ width: 150 }} />
                <Skeleton.Input active size="default" style={{ width: 200 }} />
              </Flex>
            </SectionHeaderStyled>
            <Card bodyStyle={{ padding: 0, width: "100%" }}>
              <Skeleton active paragraph={{ rows: 8 }} />
            </Card>
          </>
        ) : (
          <>
            <SectionHeaderStyled>
              <MediumLargeHeadingStyled>{importDataPage?.select_Periods?.title || "Periodos de matrícula"}</MediumLargeHeadingStyled>
              <Flex style={{ gap: "15px" }}>
                <Button onClick={() => setDrawerVerificacionOpen(true)}>{importDataPage?.select_Periods?.verify_button || "Verificación de reglas"}</Button>
                <Form.Item label={importDataPage?.select_Periods?.filter_label || "Filtro por año"}>
                  <Input
                    type="number"
                    size="small"
                    defaultValue={anioFiltro}
                    onChange={(e) => setAnioFiltro(+e.target.value)}
                    placeholder={importDataPage?.year_filter_placeholder || "Año"}
                    style={{ height: "35px", marginRight: "15px" }}
                  />
                </Form.Item>
              </Flex>
            </SectionHeaderStyled>
            <Card bodyStyle={{ padding: 0, width: "100%" }}>
              <Table<IPeriodosFechas>
                loading={isLoading || isLoadingCreate || isLoadingUpdate}
                columns={columns}
                dataSource={data?.data}
                rowKey={(v) => v.procesoId}
                pagination={{
                  defaultPageSize: 5,
                  showSizeChanger: true,
                  style: { marginRight: "30px" },
                }}
                scroll={{ x: 800 }}
              />
            </Card>
            <Drawer
            placement="right"
            title={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 300 }} /> : (importDataPage?.select_Periods?.subtitleO || "Verificación de reglas de estimación según ID")}
            open={drawerVerificacionOpen}
            onClose={() => {
              setMatriculaVerificacion(undefined);
              setPeriodoVerificacion(undefined);
              setCurriculumVerificacion(undefined);
              setDrawerVerificacionOpen(false);
            }}
          >
            <Form
              form={form}
              layout="vertical"
              ref={formRef}
              onFinish={() =>
                getRulesVerification({
                  matriculaVerificacion,
                  periodoVerificacion,
                  anioVerificacion,
                  curriculumVerificacion,
                })
              }
              style={{ display: "flex", flexDirection: "column", gap: 16 }}
              initialValues={{
                id: 1,
              }}
            >
              <Form.Item
                name="matricula"
                label={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 200 }} /> : (importDataPage?.select_Periods?.studentID || "ID de estudiante")}
                hasFeedback
                initialValue={matriculaVerificacion}
                rules={[
                  {
                    required: true,
                    message: importDataPage?.required_field_message || "Este campo es requerido",
                  },
                ]}
              >
                <Input
                  onChange={(e) => {
                    setMatriculaVerificacion(e.target.value);
                    getCareersByStudentId(e.target.value);
                  }}
                  value={matriculaVerificacion}
                />
              </Form.Item>
              <Form.Item
                name="carrera"
                label={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 200 }} /> : (importDataPage?.select_Periods?.resumen || "Currículum")}
                hasFeedback
                rules={[
                  {
                    required: true,
                    message: importDataPage?.required_field_message || "Este campo es requerido",
                  },
                ]}
              >
                <Select
                  size="large"
                  allowClear
                  options={carrerasOptions}
                  onChange={(e) => setCurriculumVerificacion(e)}
                  value={curriculumVerificacion}
                  loading={isLoadingCareers}
                  placeholder={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (importDataPage?.select_Periods?.program || "Carrera")}
                />
              </Form.Item>
              <Form.Item
                name="anio"
                label={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 200 }} /> : (importDataPage?.select_Periods?.year || "Año")}
                initialValue={anioVerificacion}
                rules={[
                  {
                    required: true,
                    message: importDataPage?.required_field_message || "Este campo es requerido",
                  },
                ]}
              >
                <InputNumber onChange={(e) => setAnioVerificacion(e ? +e : 0)} controls={false} />
              </Form.Item>
              <Form.Item
                name="periodoId"
                label={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 200 }} /> : (importDataPage?.select_Periods?.period || "Periodo")}
                hasFeedback
                rules={[
                  {
                    required: true,
                    message: importDataPage?.required_field_message || "Este campo es requerido",
                  },
                ]}
              >
                <Select
                  options={periodosOptions}
                  filterOption={filterOption}
                  placeholder={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (importDataPage?.select_Periods?.select_option || "Seleccione un periodo")}
                  showSearch
                  optionFilterProp="children"
                  allowClear
                  onChange={(e) => setPeriodoVerificacion(e)}
                  value={periodoVerificacion}
                />
              </Form.Item>

              <Button
                type="primary"
                size="large"
                htmlType="submit"
                style={{ alignSelf: "flex-end" }}
                loading={isLoadingVerification}
              >
                {isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 150 }} /> : (importDataPage?.select_Periods?.verify_stimation_button || "Verificar reglas de estimación")}
              </Button>
              {rulesVerification?.data && (
                <>
                  <Divider orientation="left">{`${importDataPage?.select_Periods?.assigned_rules_prefix || "Reglas asignadas a"} ${matriculaVerificacion}`}</Divider>
                  <List
                    dataSource={Object.keys(rulesVerification.data).map((rule) => ({
                      reglaNombre: rule,
                      regla: rulesVerification.data[rule],
                    }))}
                    renderItem={(item: any) => (
                      <List.Item>
                        <Text mark>{item.reglaNombre}</Text> {item.regla}
                      </List.Item>
                    )}
                  />
                </>
              )}
            </Form>
          </Drawer>
          <Drawer
            placement="right"
            title={
              procesoId !== 0
                ? isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 300 }} /> : `${importDataPage?.select_Periods?.subtitleT || "Edición de periodo de selección"} ${data?.data?.find(
                    (d: IPeriodosFechas) => d.procesoId === procesoId,
                  )?.periodoNombre}`
                : importDataPage?.select_Periods?.new_period_subtitle || "Nuevo periodo de selección"
            }
            open={drawerOpen}
            onClose={() => {
              setProcesoId(0);
              setDrawerOpen(false);
            }}
          >
            <Form
              form={form}
              layout="vertical"
              ref={formRef}
              onFinish={submit}
              style={{ display: "flex", flexDirection: "column", gap: 16 }}
              initialValues={{
                id: 1,
              }}
            >
              {/* <Form.Item name="anio" label="Año" hasFeedback>
                <Input type="number" min={0} onChange={(e) => setAnioForm(+e.target.value)} />
              </Form.Item>
              <Form.Item name="periodoId" label="Periodo" hasFeedback>
                <Select size="large" options={periodosOptions} placeholder="Periodo" showSearch />
              </Form.Item>
              <Form.Item name="fechaInicio" label="Fechas límites" hasFeedback>
                <DatePicker.RangePicker format={"DD/MM/YYYY"} />
              </Form.Item>
              <Form.Item
                name="limitadoACarreras"
                label="¿El proceso está limitado a carreras?"
                hasFeedback
              >
                <Radio.Group
                  options={[
                    { label: "Si", value: true },
                    { label: "No", value: false },
                  ]}
                  onChange={onChangeLimitadoACarreras}
                />
              </Form.Item>
              {isCareersSelectVisible && (
                <Form.Item
                  name="carreras"
                  label="Carreras para las que está disponible"
                  hasFeedback
                >
                  <Select
                    size="large"
                    options={carrerasOptions}
                    mode="multiple"
                    placeholder="Carreras para la selección"
                  />
                </Form.Item>
              )} */}

              <Form.Item
                name="terminosYCondiciones"
                label={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 200 }} /> : (importDataPage?.select_Periods?.termcondition || "Términos y condiciones")}
                hasFeedback
                rules={[
                  {
                    required: true,
                    message: importDataPage?.required_field_message || "Este campo es requerido",
                  },
                ]}
              >
                {drawerOpen && (
                  <ConfigurationInput
                    data={{
                      id: 1,
                      label: "",
                      nombre: "terminosYCondiciones",
                      tipo: "Editor",
                      valor: form.getFieldValue("terminosYCondiciones"),
                      descripcion: importDataPage?.select_Periods?.terms_description || "Específique los términos y condiciones según el periodo.",
                    }}
                    onChange={(value) => form.setFieldValue("terminosYCondiciones", value ?? null)}
                  />
                )}
              </Form.Item>
              <Collapse
                items={[
                  {
                    key: 1,
                    label: (
                      <SmallHeadingStyled style={{ margin: 0 }}>
                        {isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 200 }} /> : (importDataPage?.select_Periods?.estimation_rules || "Reglas de estimación")}
                      </SmallHeadingStyled>
                    ),
                    children: (
                      <div>
                        {" "}
                        <Form.Item
                          name="reglaContado"
                          label={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 200 }} /> : (importDataPage?.select_Periods?.rule_one || "Pago único (colones)")}
                          required
                          rules={[
                            {
                              required: true,
                              message: importDataPage?.select_Periods?.required_message || "Este campo es requerido.",
                            },
                          ]}
                          style={{ marginBottom: 4 }}
                        >
                          <Select
                            allowClear
                            placeholder={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (importDataPage?.select_Periods?.select_option || "Seleccionar moneda")}
                            size="large"
                            options={rulesData?.data?.colones?.map((ru: any) => ({
                              label: translatedData.reglas[ru.descripcion] || ru.descripcion,
                              value: ru.id,
                            }))}
                          />
                        </Form.Item>
                        <Form.Item
                          name="reglaContadoAmazon"
                          label={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 200 }} /> : (importDataPage?.select_Periods?.rule_two || "Pago único con Amazon (colones)")}
                          required
                          rules={[
                            {
                              required: true,
                              message: importDataPage?.select_Periods?.required_message || "Este campo es requerido.",
                            },
                          ]}
                          style={{ marginBottom: 4 }}
                        >
                          <Select
                            allowClear
                            placeholder={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (importDataPage?.select_Periods?.select_option || "Seleccionar moneda")}
                            size="large"
                            options={rulesData?.data?.colones?.map((ru: any) => ({
                              label: translatedData.reglas[ru.descripcion] || ru.descripcion,
                              value: ru.id,
                            }))}
                          />
                        </Form.Item>
                        <Form.Item
                          name="reglaCuotas"
                          label={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 200 }} /> : (importDataPage?.select_Periods?.rule_three || "Pago a cuotas (colones)")}
                          style={{ marginBottom: 4 }}
                        >
                          <Select
                            allowClear
                            placeholder={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (importDataPage?.select_Periods?.select_option || "Seleccionar moneda")}
                            size="large"
                            options={rulesData?.data?.colones?.map((ru: any) => ({
                              label: translatedData.reglas[ru.descripcion] || ru.descripcion,
                              value: ru.id,
                            }))}
                          />
                        </Form.Item>
                        <Form.Item
                          name="reglaCuotasAmazon"
                          label={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 200 }} /> : (importDataPage?.select_Periods?.rule_four || "Pago a cuotas con Amazon (colones)")}
                          style={{ marginBottom: 4 }}
                        >
                          <Select
                            allowClear
                            placeholder={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (importDataPage?.select_Periods?.select_option || "Seleccionar moneda")}
                            size="large"
                            options={rulesData?.data?.colones?.map((ru: any) => ({
                              label: translatedData.reglas[ru.descripcion] || ru.descripcion,
                              value: ru.id,
                            }))}
                          />
                        </Form.Item>
                        <Form.Item
                          name="reglaContadoDolares"
                          label={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 200 }} /> : (importDataPage?.select_Periods?.rule_five || "Pago único (dólares)")}
                          required
                          rules={[
                            {
                              required: true,
                              message: importDataPage?.select_Periods?.required_message || "Este campo es requerido.",
                            },
                          ]}
                          style={{ marginBottom: 4 }}
                        >
                          <Select
                            allowClear
                            placeholder={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (importDataPage?.select_Periods?.select_option || "Seleccionar moneda")} 
                            size="large"
                            options={rulesData?.data?.dolares?.map((ru: any) => ({
                              label: translatedData.reglas[ru.descripcion] || ru.descripcion,
                              value: ru.id,
                            }))}
                          />
                        </Form.Item>
                        <Form.Item
                          name="reglaContadoAmazonDolares"
                          label={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 200 }} /> : (importDataPage?.select_Periods?.rule_six || "Pago único con Amazon (dólares)")}
                          required
                          rules={[
                            {
                              required: true,
                              message: importDataPage?.select_Periods?.required_message || "Este campo es requerido.",
                            },
                          ]}
                          style={{ marginBottom: 4 }}
                        >
                          <Select
                            allowClear
                            placeholder={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (importDataPage?.select_Periods?.select_option || "Seleccionar moneda")}
                            size="large"
                            options={rulesData?.data?.dolares?.map((ru: any) => ({
                              label: translatedData.reglas[ru.descripcion] || ru.descripcion,
                              value: ru.id,
                            }))}
                          />
                        </Form.Item>
                        <Form.Item
                          name="reglaCuotasDolares"
                          label={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 200 }} /> : (importDataPage?.select_Periods?.rule_seven || "Pago a cuotas (dólares)")}
                          style={{ marginBottom: 4 }}
                        >
                          <Select
                            allowClear
                            placeholder={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (importDataPage?.select_Periods?.select_option || "Seleccionar moneda")}
                            size="large"
                            options={rulesData?.data?.dolares?.map((ru: any) => ({
                              label: translatedData.reglas[ru.descripcion] || ru.descripcion,
                              value: ru.id,
                            }))}
                          />
                        </Form.Item>
                        <Form.Item
                          name="reglaCuotasAmazonDolares"
                          label={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 200 }} /> : (importDataPage?.select_Periods?.rule_eight || "Pago a cuotas con Amazon (dólares)")}
                          style={{ marginBottom: 4 }}
                        >
                          <Select
                            allowClear
                            placeholder={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (importDataPage?.select_Periods?.select_option || "Seleccionar moneda")}
                            size="large"
                            options={rulesData?.data?.dolares?.map((ru: any) => ({
                              label: translatedData.reglas[ru.descripcion] || ru.descripcion,
                              value: ru.id,
                            }))}
                          />
                        </Form.Item>
                      </div>
                    ),
                  },
                ]}
                defaultActiveKey={1}
                ghost
                style={{ background: "transparent" }}
              />

              <Button
                type="primary"
                size="large"
                htmlType="submit"
                style={{ alignSelf: "flex-end" }}
                loading={isLoadingUpdate || isLoadingCreate}
              >
                {isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 150 }} /> : (importDataPage?.select_Periods?.save_button || "Guardar")}
              </Button>
            </Form>
          </Drawer>
          </>
        )}
      </Content>
    </Flex>
  ) : (
    <PeriodEstimationVariants
      goBack={() => setPeriodoVariantes(undefined)}
      periodo={periodoVariantes}
    />
  );
};

export default DateSettings;
